import React, { createContext, useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import { getBrowserTimeZone } from './lib/browserTimeZone';
import posthog from './posthogClient';

export const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const hadAuthenticatedUserRef = useRef(false);

  useEffect(() => {
    if (!process.env.REACT_APP_POSTHOG_KEY) return;

    if (user) {
      hadAuthenticatedUserRef.current = true;
      posthog.identify(user.id, {
        email: user.email ?? undefined,
      });
      return;
    }

    if (hadAuthenticatedUserRef.current) {
      posthog.reset();
      hadAuthenticatedUserRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    const syncBrevoContact = (session) => {
      if (!session?.access_token) return;

      void fetch('/api/brevo-contact', {
        method: 'POST',
        keepalive: true,
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'sync', timezone: getBrowserTimeZone() }),
      })
        .then((response) => {
          if (!response.ok) {
            console.warn('Brevo session sync failed:', response.status);
          }
        })
        .catch((error) => {
          console.warn('Brevo session sync error:', error);
        });
    };

    const fetchUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      syncBrevoContact(session);
    };

    fetchUser();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setUser(session?.user ?? null);
        if (event === 'SIGNED_IN') {
          syncBrevoContact(session);
        }
      }
    );

    return () => {
      if (authListener && authListener.subscription) {
        authListener.subscription.unsubscribe();
      }
    };
  }, []);

  return (
    <UserContext.Provider value={{ user, setUser }}>
      {children}
    </UserContext.Provider>
  );
};
